import React, { useState } from 'react';
import {
  BookOpen, Users, Building2, Package, Search, MessageSquare,
  CreditCard, Sparkles, Globe, Shield, LayoutDashboard, Star,
  MapPin, BarChart2, FileText, Zap, Bell, Mail, Calendar,
  ChevronDown, ChevronUp, CheckCircle2, Clock, ArrowUpRight,
  Briefcase, Map, List, Filter, Award, Tag, ExternalLink, Flame
} from 'lucide-react';

const STATUS = {
  done: { label: 'Tamamlandı', color: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  partial: { label: 'Kısmi', color: 'bg-amber-100 text-amber-700 border border-amber-200' },
  planned: { label: 'Planlandı', color: 'bg-gray-100 text-gray-600 border border-gray-200' },
};

// Bir tarih son 30 gün içinde mi?
const isNew = (dateStr) => {
  if (!dateStr) return false;
  const diff = (new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24);
  return diff <= 30;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const Badge = ({ status }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS[status]?.color}`}>
    {STATUS[status]?.label}
  </span>
);

const NewiBadge = () => (
  <span className="inline-flex items-center gap-0.5 text-[10px] font-black px-2 py-0.5 rounded-full bg-rose-500 text-white whitespace-nowrap animate-pulse">
    <Flame className="w-2.5 h-2.5" /> YENİ
  </span>
);

const FeatureItem = ({ text, status = 'done', note }) => (
  <li className="flex items-start gap-2 py-1">
    <CheckCircle2 className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${status === 'done' ? 'text-emerald-500' : status === 'partial' ? 'text-amber-400' : 'text-gray-300'}`} />
    <div className="flex-1 min-w-0">
      <span className="text-sm text-gray-700">{text}</span>
      {note && <p className="text-xs text-gray-400 mt-0.5 italic">{note}</p>}
    </div>
    {status !== 'done' && <Badge status={status} />}
  </li>
);

const MODULE_DATA = [
  {
    id: 'genel',
    icon: Globe,
    color: 'from-slate-600 to-slate-800',
    bg: 'bg-slate-50 border-slate-200',
    title: 'Platform Genel',
    subtitle: 'Tüm kullanıcı tiplerini kapsayan temel altyapı',
    status: 'done',
    last_updated: '2026-04-05',
    sections: [
      {
        heading: 'Kullanıcı Tipleri',
        items: [
          { text: 'Exhibitor (Katılımcı) — Fuar stand\'ı yaptıracak firma', status: 'done' },
          { text: 'Contractor (Müteahhit) — Stand tasarım & üretim firması', status: 'done' },
          { text: 'Supplier (Tedarikçi) — Malzeme, baskı, mobilya sağlayıcı', status: 'done' },
          { text: 'Ultra Admin — Tüm platform yöneticisi', status: 'done' },
        ]
      },
      {
        heading: 'Kimlik Doğrulama',
        items: [
          { text: 'Kayıt / Giriş — her kullanıcı tipi için ayrı sayfalar', status: 'done' },
          { text: 'JWT tabanlı oturum yönetimi', status: 'done' },
          { text: 'Şifremi Unuttum — e-posta ile sıfırlama', status: 'done' },
          { text: 'Beni Hatırla (7 gün) — localStorage kalıcı oturum', status: 'done' },
          { text: 'Admin Kimliğine Bürünme (Impersonation) — admin kullanıcı adına sisteme giriş', status: 'done' },
        ]
      },
      {
        heading: 'Profil & Hesap',
        items: [
          { text: 'Organizasyon profili (logo, hakkında, konum)', status: 'done' },
          { text: 'Profil tamamlanma skoru (%0-100)', status: 'done' },
          { text: 'Alt kullanıcı (Ekip) yönetimi — davet, rol atama', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'exhibitor',
    icon: Briefcase,
    color: 'from-blue-600 to-blue-800',
    bg: 'bg-blue-50 border-blue-200',
    title: 'Exhibitor (Katılımcı) Modülü',
    subtitle: 'Fuar stand\'ı yaptıracak firmaların kullandığı modül',
    status: 'done',
    last_updated: '2026-04-08',
    sections: [
      {
        heading: 'Dashboard',
        items: [
          { text: 'KPI kartları: Aktif Brief, Alınan Teklif, Ödüllendirilen Proje, Toplam Bütçe', status: 'done' },
          { text: 'Haftalık aktivite grafiği (brief & teklif trendi)', status: 'done' },
          { text: 'Profil Tamamlanma widget\'ı', status: 'done' },
          { text: 'Son Brief\'ler ve Son Teklifler listesi', status: 'done' },
          { text: 'Aktivite Serisi (streak) widget\'ı', status: 'done' },
        ]
      },
      {
        heading: 'AI Contractor Önerisi',
        items: [
          { text: '"Önerilen Contractor\'lar" bölümü — gerçek kontraktor veritabanından seçim', status: 'done' },
          { text: 'Her contractor için AI puanı (1-5 yıldız) + açıklama', status: 'done', note: 'Gemini/OpenAI ile — 1 saatlik sessionStorage cache' },
          { text: 'Mükemmel / Yüksek / Orta etiketleri', status: 'done' },
        ]
      },
      {
        heading: 'Brief Yönetimi',
        items: [
          { text: 'Yeni Brief oluşturma formu (funar, şehir, bütçe, tarih, açıklama)', status: 'done' },
          { text: 'Brief listesi — durum filtreleme', status: 'done' },
          { text: 'Brief detayı ve teklifler', status: 'done' },
          { text: 'Contractor\'ı Brief\'e davet etme', status: 'done' },
          { text: 'Brief\'ten doğrudan mesaj açma ("Brief\'ten Mesaj Aç")', status: 'done' },
        ]
      },
      {
        heading: 'Teklif (Proposal) Yönetimi',
        items: [
          { text: 'Gelen teklifleri listeleme', status: 'done' },
          { text: 'Teklif detayı — fiyat, açıklama, dosyalar', status: 'done' },
          { text: 'Teklif kabul / red', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'contractor',
    icon: Building2,
    color: 'from-emerald-600 to-emerald-800',
    bg: 'bg-emerald-50 border-emerald-200',
    title: 'Contractor (Müteahhit) Modülü',
    subtitle: 'Stand tasarım & üretim firmalarının kullandığı modül',
    status: 'done',
    last_updated: '2026-04-08',
    sections: [
      {
        heading: 'Dashboard',
        items: [
          { text: 'KPI: Aktif Brief, Gönderilen Teklif, Kabul Edilen Teklif, Toplam Gelir', status: 'done' },
          { text: 'Aylık gelir grafiği', status: 'done' },
          { text: 'Brief Board — Tüm platform brief\'leri (kart/liste görünümü)', status: 'done' },
          { text: 'Teklif takip tablosu', status: 'done' },
        ]
      },
      {
        heading: 'AI Brief Eşleştirme',
        items: [
          { text: '"AI Brief Önerileri" widget\'ı — en uygun briefleri listeler', status: 'done' },
          { text: 'Her brief için uyum skoru ve açıklama', status: 'done', note: 'Gemini/OpenAI ile — 1 saatlik sessionStorage cache' },
          { text: 'Puan: Yüksek / Orta / Düşük uyum etiketleri', status: 'done' },
        ]
      },
      {
        heading: 'Teklif Gönderme',
        items: [
          { text: 'Brief\'e fiyat + açıklama + dosya ile teklif gönderme', status: 'done' },
          { text: 'Gönderilen teklifleri takip etme', status: 'done' },
        ]
      },
      {
        heading: 'Public Profil Sayfası',
        items: [
          { text: 'Herkese açık contractor profili (/contractor/:slug)', status: 'done' },
          { text: 'Sekmeler: Genel Bakış, Portfolio, Hizmetler, Coverage (ülkeler + şehirler), Referanslar', status: 'done' },
          { text: 'Coverage haritası: Kapsadığı ülkeler, şehirler ve Active Shows (fuar rozetleri)', status: 'done' },
          { text: '"Brief Gönder" / "Quote İste" formu', status: 'done' },
          { text: 'SEO başlığı: "Firma Adı — Exhibition Stand Contractor | ExpoStand"', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'supplier',
    icon: Package,
    color: 'from-purple-600 to-purple-800',
    bg: 'bg-purple-50 border-purple-200',
    title: 'Tedarikçi (Supplier) Modülü',
    subtitle: 'Malzeme, baskı, mobilya ve ekipman sağlayıcıları',
    status: 'done',
    last_updated: '2026-04-07',
    sections: [
      {
        heading: 'Dashboard',
        items: [
          { text: 'KPI kartları: Aktif Sipariş, Toplam Ürün, Gelir', status: 'done' },
          { text: 'Kategori dağılım grafiği', status: 'done' },
        ]
      },
      {
        heading: 'Fatura Yönetimi',
        items: [
          { text: 'Fatura oluşturma ve listeleme', status: 'done' },
          { text: 'Vadesi geçmiş fatura otomatik güncelleme (APScheduler)', status: 'done' },
          { text: 'Fatura durum takibi: Taslak / Gönderildi / Ödendi / Gecikmiş', status: 'done' },
        ]
      },
      {
        heading: 'Public Profil Sayfası',
        items: [
          { text: 'Herkese açık supplier profili (/supplier/:slug)', status: 'done' },
          { text: 'Sekmeler: Genel Bakış, Ürün Kataloğu, Teslimat & Coverage, Sertifikalar', status: 'done' },
          { text: 'Gerçek API\'den katalog ürünleri (catalog.items)', status: 'done' },
          { text: 'Coverage Tab: Teslimat ülkeleri + şehirler + Active Shows rozetleri', status: 'done' },
          { text: 'SEO başlığı: "Firma Adı — Exhibition Supplier | ExpoStand"', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'search',
    icon: Search,
    color: 'from-orange-500 to-orange-700',
    bg: 'bg-orange-50 border-orange-200',
    title: 'Arama & Keşif Motoru',
    subtitle: 'Herkese açık, giriş gerektirmeyen arama sistemi',
    status: 'done',
    last_updated: '2026-04-12',
    sections: [
      {
        heading: 'Ana Sayfa Arama Formu',
        items: [
          { text: 'Contractor / Supplier toggle butonları', status: 'done' },
          { text: 'Servis, Şehir, Fuar arama alanları', status: 'done' },
          { text: 'NEXT butonu → doğru sayfaya yönlendirme', status: 'done' },
        ]
      },
      {
        heading: 'Contractor Arama Sayfası (/find-contractors)',
        items: [
          { text: 'Servis, Şehir, Fuar, Şirket adı filtreleri', status: 'done' },
          { text: 'Coverage bazlı şehir filtresi (coverage.countries[].cities)', status: 'done', note: 'Bir contractor\'ın Frankfurt coverage\'ı varsa "Frankfurt" aramasında çıkar' },
          { text: 'Fair filter STRICT — yalnızca o fuarda aktif olan contractor\'lar', status: 'done' },
          { text: 'Active Filter badge\'leri (X ile kaldırılabilir)', status: 'done' },
          { text: 'Liste / Harita / Split (yan yana) görünüm', status: 'done' },
          { text: 'Harita: Leaflet + MarkerCluster (aynı şehirdeki pinler gruplandırılır)', status: 'done' },
          { text: 'Contractor kartlarında ⭐ Active Shows rozetleri', status: 'done' },
          { text: 'Karşılaştırma checkbox\'ı + "Compare Now →" bar', status: 'done' },
          { text: 'Giriş yapmayan kullanıcılar için "Register to see" CTA', status: 'done' },
        ]
      },
      {
        heading: 'Supplier Arama Sayfası (/find-suppliers)',
        items: [
          { text: 'Ürün, Şehir, Ülke, Fuar filtreleri', status: 'done' },
          { text: 'Liste / Harita / Split görünüm', status: 'done' },
          { text: 'Supplier kartlarında kategori etiketleri + Active Shows', status: 'done' },
          { text: 'Popular fairs quick-link butonları', status: 'done' },
        ]
      },
      {
        heading: 'SEO Sayfaları (URL Yapısı)',
        items: [
          { text: '/find-contractors/istanbul — şehir bazlı önceden doldurulmuş sayfa', status: 'done' },
          { text: '/find-contractors/fair/hannover-messe — fuar bazlı SEO sayfası', status: 'done' },
          { text: '/find-suppliers/istanbul — supplier şehir sayfası', status: 'done' },
          { text: '/find-suppliers/fair/ise-amsterdam — supplier fuar sayfası', status: 'done' },
          { text: 'Dinamik <title> ve <meta description> (react-helmet-async)', status: 'done' },
          { text: 'Canonical URL otomatik atama', status: 'done' },
        ]
      },
      {
        heading: 'Contractor Karşılaştırma (/compare-contractors)',
        items: [
          { text: 'Listeden 2-3 contractor seçimi', status: 'done' },
          { text: 'Yan yana karşılaştırma tablosu', status: 'done' },
          { text: 'Karşılaştırılan alanlar: Profil Skoru, Konum, Ülke Sayısı, Hizmetler, Active Shows, Teklif Sayısı, Kazanma Oranı, Plan, Hakkında', status: 'done' },
          { text: 'URL paylaşılabilir (?slugs=a,b,c)', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'messaging',
    icon: MessageSquare,
    color: 'from-teal-600 to-teal-800',
    bg: 'bg-teal-50 border-teal-200',
    title: 'Mesajlaşma Sistemi',
    subtitle: 'Platformlar arası gerçek zamanlı mesajlaşma',
    status: 'done',
    last_updated: '2026-04-06',
    sections: [
      {
        heading: 'Özellikler',
        items: [
          { text: 'Exhibitor ↔ Contractor gerçek zamanlı mesajlaşma', status: 'done' },
          { text: 'Okunmamış mesaj sayacı (header\'da badge)', status: 'done' },
          { text: 'Brief bağlamı banner\'ı — mesajlaşmaya hangi brief\'ten gelindiği gösterilir', status: 'done' },
          { text: '"Brief\'ten Mesaj Aç" butonu — Brief sayfasından doğrudan konuşma başlatma', status: 'done' },
          { text: 'Konuşma listesi + mesaj geçmişi', status: 'done' },
          { text: 'Dosya eki (attachment) — Temel destek', status: 'partial', note: 'Dosya yükleme altyapısı mevcut ama tam mesajlaşma entegrasyonu eksik' },
        ]
      }
    ]
  },
  {
    id: 'billing',
    icon: CreditCard,
    color: 'from-rose-600 to-rose-800',
    bg: 'bg-rose-50 border-rose-200',
    title: 'Faturalama & Ödeme (Stripe)',
    subtitle: 'Abonelik planları, ödeme akışı ve webhook yönetimi',
    status: 'done',
    last_updated: '2026-04-09',
    sections: [
      {
        heading: 'Abonelik Planları',
        items: [
          { text: '3 plan: Free, Pro, Enterprise', status: 'done' },
          { text: 'Plan özellikleri karşılaştırma sayfası', status: 'done' },
          { text: 'Stripe Checkout entegrasyonu', status: 'done' },
        ]
      },
      {
        heading: 'Webhook Entegrasyonu',
        items: [
          { text: 'checkout.session.completed → abonelik aktivasyonu', status: 'done' },
          { text: 'customer.subscription.updated → plan güncelleme', status: 'done' },
          { text: 'customer.subscription.deleted → Free\'ye düşme', status: 'done' },
          { text: 'invoice.paid → abonelik devam', status: 'done' },
          { text: 'Webhook sonrası db.orgs.plan_code senkronizasyonu', status: 'done', note: 'Ödeme gelince public arama sıralaması da güncellenir' },
        ]
      },
      {
        heading: 'Admin Abonelik Yönetimi',
        items: [
          { text: 'Tüm abonelikleri listeleme, filtreleme, durum takibi', status: 'done' },
          { text: 'Elle plan değiştirme', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'ai',
    icon: Sparkles,
    color: 'from-violet-600 to-violet-800',
    bg: 'bg-violet-50 border-violet-200',
    title: 'Yapay Zeka (AI) Özellikleri',
    subtitle: 'Gemini & OpenAI ile akıllı eşleştirme ve öneri sistemi',
    status: 'done',
    last_updated: '2026-04-10',
    sections: [
      {
        heading: 'Exhibitor için AI Contractor Önerisi',
        items: [
          { text: 'Exhibitor\'ın aktif brief\'lerine göre en uygun contractor\'ları puanlar', status: 'done' },
          { text: 'Her contractor için 1-5 yıldız skor + Türkçe açıklama', status: 'done' },
          { text: 'API: POST /api/exhibitor/contractors/ai-recommend', status: 'done' },
          { text: '1 saatlik sessionStorage cache (gereksiz API çağrıları önlenir)', status: 'done' },
        ]
      },
      {
        heading: 'Contractor için AI Brief Eşleştirme',
        items: [
          { text: 'Contractor profiline göre en uygun brief\'leri puanlar', status: 'done' },
          { text: 'Her brief için uyum yüzdesi + açıklama', status: 'done' },
          { text: 'API: POST /api/contractor/briefs/ai-match', status: 'done' },
          { text: '1 saatlik sessionStorage cache', status: 'done' },
        ]
      },
      {
        heading: 'Teknik Altyapı',
        items: [
          { text: 'emergentintegrations kütüphanesi (Universal Key)', status: 'done' },
          { text: 'LlmChat + UserMessage + send_message() pattern', status: 'done' },
          { text: 'Gemini Flash ve OpenAI GPT-4o desteği', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'admin',
    icon: Shield,
    color: 'from-gray-700 to-gray-900',
    bg: 'bg-gray-50 border-gray-200',
    title: 'Ultra Admin Paneli',
    subtitle: 'Tüm platformu yönetmek için gelişmiş admin arayüzü',
    status: 'done',
    last_updated: '2026-04-11',
    sections: [
      {
        heading: 'Ana Dashboard',
        items: [
          { text: 'Canlı platform istatistikleri (Toplam Gelir, Aktif Kullanıcı, Brief Sayısı, Dönüşüm Oranı)', status: 'done' },
          { text: 'Aylık gelir ve kullanıcı büyüme grafikleri', status: 'done' },
          { text: 'Plan dağılımı (Free/Pro/Enterprise pasta grafik)', status: 'done' },
          { text: 'Coğrafi dağılım haritası', status: 'done' },
          { text: 'Son aktiviteler ve uyarılar', status: 'done' },
        ]
      },
      {
        heading: 'Organizasyon Yönetimi',
        items: [
          { text: 'Tüm org\'ları listeleme, filtreleme, arama', status: 'done' },
          { text: 'Org detayı, plan değiştirme, askıya alma', status: 'done' },
          { text: 'Featured (öne çıkarma) özelliği', status: 'done' },
          { text: 'Kimliğe bürünme (impersonate) — kullanıcı hesabına erişim', status: 'done' },
        ]
      },
      {
        heading: 'Kullanıcı Yönetimi',
        items: [
          { text: 'Sol: Şirket listesi, Sağ: Seçili şirketin kullanıcıları (split layout)', status: 'done' },
          { text: 'Kullanıcı detayı, rol değiştirme, devre dışı bırakma', status: 'done' },
          { text: 'Yeni kullanıcı daveti', status: 'done' },
        ]
      },
      {
        heading: 'Plan & Abonelik Yönetimi',
        items: [
          { text: 'Planları düzenleme (özellikler, fiyat, limit)', status: 'done' },
          { text: 'Abonelikleri listele, filtrele, elle değiştir', status: 'done' },
        ]
      },
      {
        heading: 'Denetim & Webhook',
        items: [
          { text: 'Denetim logları — tüm sistem aktiviteleri', status: 'done' },
          { text: 'Webhook yönetimi — URL, event tipi, aktif/pasif', status: 'done' },
          { text: 'Sistem ayarları sayfası', status: 'done' },
        ]
      },
      {
        heading: 'İçerik Yönetimi',
        items: [
          { text: 'Ana Sayfa içerik yönetimi (CMS)', status: 'done' },
          { text: 'Fuar veritabanı (11.900+ fuar)', status: 'done' },
          { text: 'Fuar organizatörleri yönetimi', status: 'done' },
          { text: 'Dropdown yönetimi (ülke, şehir, para birimi)', status: 'done' },
          { text: 'Email Scheduler — otomatik kampanya zamanlaması', status: 'done' },
          { text: 'Platform kuralları sayfası', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'seo',
    icon: Globe,
    color: 'from-cyan-600 to-cyan-800',
    bg: 'bg-cyan-50 border-cyan-200',
    title: 'SEO & Büyüme Altyapısı',
    subtitle: 'Organik trafik için teknik SEO optimizasyonları',
    status: 'done',
    last_updated: '2026-04-12',
    sections: [
      {
        heading: 'Dinamik Meta Taglar',
        items: [
          { text: 'Her sayfaya özel <title> ve <meta description>', status: 'done' },
          { text: 'Open Graph (og:title, og:description, og:image)', status: 'done' },
          { text: 'Canonical URL ataması', status: 'done' },
        ]
      },
      {
        heading: 'URL Yapısı (SEO-Friendly)',
        items: [
          { text: '/find-contractors/istanbul → "Istanbul\'da Stand Contractor"', status: 'done' },
          { text: '/find-contractors/fair/hannover-messe → "Hannover Messe Stand Contractor"', status: 'done' },
          { text: '/find-suppliers/istanbul → "Istanbul\'da Tedarikçi"', status: 'done' },
          { text: '/contractor/:slug → şirket ismiyle slug URL', status: 'done' },
          { text: '/supplier/:slug → tedarikçi slug URL', status: 'done' },
        ]
      },
      {
        heading: 'İçerik SEO',
        items: [
          { text: 'Contractor profil sayfalarında Active Shows rozetleri (internal linking)', status: 'done' },
          { text: 'Fuar sayfalarında "Popular Fairs" hızlı linkler', status: 'done' },
          { text: 'Coverage Tab\'da fuar linklerinden arama sayfasına yönlendirme', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'landing',
    icon: Star,
    color: 'from-amber-500 to-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    title: 'Landing & Tanıtım Sayfaları',
    subtitle: 'Yeni kullanıcıları platforma çeken genel sayfalar',
    status: 'done',
    last_updated: '2026-04-04',
    sections: [
      {
        heading: 'Hedef Kitleye Özel Sayfalar',
        items: [
          { text: 'Exhibitor Landing — "Fuar standınız için teklif alın"', status: 'done' },
          { text: 'Contractor Landing — "Platform\'a katılın, brief\'lere erişin"', status: 'done' },
          { text: 'Supplier Landing — "Ürünlerinizi sergileyin"', status: 'done' },
          { text: 'Quote Form — Üye olmadan teklif talebi', status: 'done' },
        ]
      },
      {
        heading: 'Şehir & Kategori Sayfaları',
        items: [
          { text: 'Dinamik şehir sayfaları (11 şehir)', status: 'done' },
          { text: 'Top 10 Contractor\'lar sayfası', status: 'done' },
          { text: 'Kategori landing sayfaları', status: 'done' },
        ]
      }
    ]
  },
  {
    id: 'technical',
    icon: Zap,
    color: 'from-indigo-600 to-indigo-800',
    bg: 'bg-indigo-50 border-indigo-200',
    title: 'Teknik Altyapı',
    subtitle: 'Backend, veritabanı ve sistem mimarisi',
    status: 'done',
    last_updated: '2026-04-09',
    sections: [
      {
        heading: 'Backend',
        items: [
          { text: 'FastAPI (Python) — REST API', status: 'done' },
          { text: 'MongoDB (Motor async) — NoSQL veritabanı', status: 'done' },
          { text: 'Modüler route yapısı: /auth, /exhibitor, /contractor, /supplier, /public, /billing, /ultra', status: 'done' },
          { text: 'APScheduler — zamanlanmış görevler (vadesi geçmiş fatura güncelleme)', status: 'done' },
          { text: 'Pydantic modelleri — ObjectId JSON serializasyon koruması', status: 'done' },
        ]
      },
      {
        heading: 'Frontend',
        items: [
          { text: 'React 18 — SPA', status: 'done' },
          { text: 'Tailwind CSS — utility-first styling', status: 'done' },
          { text: 'Shadcn/UI — component kütüphanesi', status: 'done' },
          { text: 'React Router v6 — BrowserRouter', status: 'done' },
          { text: 'AuthContext — global kimlik doğrulama state', status: 'done' },
          { text: 'react-leaflet + react-leaflet-cluster — interaktif harita', status: 'done' },
          { text: 'react-helmet-async — dinamik SEO meta tagları', status: 'done' },
          { text: 'emergentintegrations — Universal LLM API key', status: 'done' },
        ]
      }
    ]
  }
];

const ModuleCard = ({ mod }) => {
  const [expanded, setExpanded] = useState(true);
  const Icon = mod.icon;
  const totalItems = mod.sections.reduce((acc, s) => acc + s.items.length, 0);
  const doneItems = mod.sections.reduce((acc, s) => acc + s.items.filter(i => i.status === 'done').length, 0);
  const yeni = isNew(mod.last_updated);

  return (
    <div className={`rounded-2xl border ${mod.bg} overflow-hidden`}>
      {/* Module Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-5 flex items-center justify-between hover:opacity-90 transition-opacity"
      >
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center shadow-sm`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-900 text-base">{mod.title}</h3>
              {yeni && <NewiBadge />}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{mod.subtitle}</p>
            {mod.last_updated && (
              <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                Son güncelleme: {formatDate(mod.last_updated)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5 bg-white/70 rounded-lg px-3 py-1.5 border border-white/50">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-xs font-bold text-gray-700">{doneItems}/{totalItems}</span>
          </div>
          {expanded
            ? <ChevronUp className="h-4 w-4 text-gray-500" />
            : <ChevronDown className="h-4 w-4 text-gray-500" />
          }
        </div>
      </button>

      {/* Module Content */}
      {expanded && (
        <div className="px-5 pb-5 space-y-5">
          {mod.sections.map((section, si) => (
            <div key={si}>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <div className="h-px flex-1 bg-gray-200" />
                {section.heading}
                <div className="h-px flex-1 bg-gray-200" />
              </h4>
              <ul className="space-y-0.5 bg-white/60 rounded-xl p-3 border border-white/50">
                {section.items.map((item, ii) => (
                  <FeatureItem key={ii} {...item} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function UltraPlatformFeatures() {
  const [filter, setFilter] = useState('all');
  const totalFeatures = MODULE_DATA.reduce((acc, m) => acc + m.sections.reduce((a, s) => a + s.items.length, 0), 0);
  const doneFeatures = MODULE_DATA.reduce((acc, m) => acc + m.sections.reduce((a, s) => a + s.items.filter(i => i.status === 'done').length, 0), 0);
  const newModules = MODULE_DATA.filter(m => isNew(m.last_updated)).length;

  // En son güncellenen modülün tarihi
  const latestDate = MODULE_DATA
    .filter(m => m.last_updated)
    .map(m => new Date(m.last_updated))
    .sort((a, b) => b - a)[0];
  const latestDateStr = latestDate
    ? latestDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const modules = filter === 'all' ? MODULE_DATA : MODULE_DATA.filter(m => m.id === filter);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-6 h-6 text-blue-400" />
              <span className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Platform Dokümantasyonu</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">ExpoStand Platform Özellikleri</h1>
            <p className="text-gray-400 text-sm max-w-xl">
              Bu sayfada, ExpoStand platformuna eklenen tüm özellikler modüllere göre sınıflandırılmış ve anlaşılır bir dille açıklanmıştır.
              Platformun ne yapabildiğini görmek ve hatırlamak için bu sayfayı kullanın.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
              <p className="text-2xl font-black text-white">{doneFeatures}</p>
              <p className="text-xs text-gray-400">Tamamlanan Özellik</p>
            </div>
            <div className="text-xs text-gray-500">/ {totalFeatures} toplam</div>
            {newModules > 0 && (
              <div className="flex items-center gap-1.5 bg-rose-500/20 border border-rose-400/30 rounded-lg px-3 py-1.5">
                <Flame className="w-3 h-3 text-rose-400" />
                <span className="text-xs font-bold text-rose-300">{newModules} yeni modül</span>
              </div>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-400">Genel Tamamlanma</span>
            <span className="text-xs font-bold text-white">{Math.round(doneFeatures / totalFeatures * 100)}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-emerald-400 rounded-full transition-all"
              style={{ width: `${Math.round(doneFeatures / totalFeatures * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Modül', value: MODULE_DATA.length, icon: LayoutDashboard, color: 'text-blue-600 bg-blue-50' },
          { label: 'AI Özelliği', value: 2, icon: Sparkles, color: 'text-violet-600 bg-violet-50' },
          { label: 'SEO Sayfası', value: '6+', icon: Globe, color: 'text-cyan-600 bg-cyan-50' },
          { label: 'API Endpoint', value: '50+', icon: Zap, color: 'text-orange-600 bg-orange-50' },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-lg font-black text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Module Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === 'all' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
        >
          Tümü ({MODULE_DATA.length})
        </button>
        {MODULE_DATA.map(m => {
          const Icon = m.icon;
          return (
            <button key={m.id} onClick={() => setFilter(m.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === m.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              <Icon className="h-3 w-3" />
              {m.title.split(' ')[0]}
            </button>
          );
        })}
      </div>

      {/* Module Cards */}
      <div className="space-y-4">
        {modules.map(mod => (
          <ModuleCard key={mod.id} mod={mod} />
        ))}
      </div>

      {/* Footer Note */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start gap-3">
        <Clock className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-gray-900 mb-1 text-sm">Son Güncelleme</p>
          <p className="text-xs text-gray-600">
            Bu dokümantasyon en son <strong>{latestDateStr}</strong> tarihinde güncellenmiştir.
            Yeni özellikler eklendikçe bu sayfa otomatik olarak güncellenir.
            Son 30 gün içinde güncellenen modüller <span className="inline-flex items-center gap-0.5 text-rose-600 font-bold"><Flame className="w-3 h-3" />YENİ</span> rozetiyle işaretlenmiştir.
          </p>
        </div>
      </div>
    </div>
  );
}
